/**
 * Why a call failed, in words a reader can use.
 *
 * supabase.functions.invoke never puts the function's own sentence in
 * error.message. It says "Edge Function returned a non-2xx status code" and
 * leaves the sentence in the response body, so every reason has to be read
 * out of error.context first.
 *
 * When the body has no sentence (a gateway page, a dropped connection, a
 * browser that gave up) the only text left is the library's own, and that is
 * never fit to show: an owner tapping a button on an agent page was shown
 * "Edge Function returned a non-2xx status code" (Manu, QA, 16 Sep 26).
 * fnErrorText replaces that wording with the caller's fallback, which is why
 * the fallback is required here rather than optional.
 *
 * fnErrorRaw is the same read with nothing replaced, for console lines where
 * the library's wording is the diagnosis.
 */

/**
 * The transport's own wording, matched tightly on purpose.
 *
 * An earlier version of this filter tested /Failed to fetch/ and /Load
 * failed/ as bare substrings, which swallowed 21 real refusals the edge
 * functions send, among them "Failed to fetch custom domains" and "Storage
 * upload failed: ..." ("upload failed" contains "load failed"). The browser
 * wording is always a whole message, so it is anchored; the Supabase wording
 * always names the Edge Function, so that stays a substring.
 *
 * Anchoring costs one case the loose version caught by accident, so it is
 * listed here on purpose: a chunk that no longer exists after a deploy. An
 * owner on a stale tab who picks a spreadsheet runs `await import('xlsx')`
 * (useAgentRequest.ts), and the browser throws "Failed to fetch dynamically
 * imported module: https://app.fly-social.com/assets/xlsx-Bq7x2a.js". A
 * hashed asset URL is the plainest possible case of wording an owner should
 * never read.
 */
const CLIENT_WORDING: RegExp[] = [
  // supabase-js: FunctionsHttpError, FunctionsFetchError, FunctionsRelayError.
  /\bEdge Function\b/i,
  /\bnon-2xx\b/i,
  // The browsers, whole message: Chrome, Safari, Firefox, React Native.
  /^(?:TypeError:\s*)?Failed to fetch\.?$/i,
  /^(?:TypeError:\s*)?Load failed\.?$/i,
  /^(?:TypeError:\s*)?NetworkError\b/i,
  /^Network request failed\.?$/i,
  // A code chunk that went away with the last deploy. The message carries the
  // asset URL, so these match a prefix rather than the whole line.
  /^(?:TypeError:\s*)?Failed to fetch dynamically imported module\b/i,
  /^(?:TypeError:\s*)?error loading dynamically imported module\b/i,
  /^(?:TypeError:\s*)?Importing a module script failed\b/i,
  /^(?:TypeError:\s*)?Failed to load module script\b/i,
];

/** True when the text is the client library's or the browser's own wording. */
export function isClientWording(text: string): boolean {
  const t = text.trim();
  return CLIENT_WORDING.some((re) => re.test(t));
}

async function readReason(err: unknown, fallback: string): Promise<string> {
  const ctx = (err as { context?: { json?: () => Promise<{ error?: string }> } } | null)?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const b = await ctx.json();
      if (b?.error) return String(b.error).slice(0, 200);
    } catch {
      /* not json */
    }
  }
  const msg = (err as Error | null)?.message;
  return String(msg || fallback).slice(0, 200);
}

/**
 * The reason a call failed, safe to show: the function's own sentence when it
 * sent one, the caller's fallback when all that came back was the library's
 * wording. `fallback` is required so every reader gets a sentence that fits
 * what they were doing.
 */
export async function fnErrorText(err: unknown, fallback: string): Promise<string> {
  const text = await readReason(err, fallback);
  return isClientWording(text) ? fallback : text;
}

/**
 * The same read with nothing replaced, for console lines. Never put this in
 * front of a reader.
 */
export async function fnErrorRaw(err: unknown, fallback = 'unknown'): Promise<string> {
  return readReason(err, fallback);
}

/**
 * The whole JSON body an edge function refused with, not just its `error`.
 * Some ops answer with a code in `error` and the sentence for the reader in
 * `message` (gbp-agent-portal enable_locations refusing on slots), so the
 * caller needs both.
 */
export async function fnErrorBody(err: unknown): Promise<Record<string, unknown> | null> {
  const ctx = (err as { context?: { json?: () => Promise<Record<string, unknown>> } } | null)?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      return await ctx.json();
    } catch {
      /* not json */
    }
  }
  return null;
}
